import { MenuItem, FormControl, Box, TextField, Alert, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { IDropdownResponse, ICreateMFAAppOptionRes, IUser } from '../../models';
import { Button, SMBSelect } from '../../components';
import {
  createMFAAuthyOption,
  createMFAAppOption,
  createMFATextOption,
  getMe,
  validateNewMFA
} from '../../fetch';
import { formatInputPhoneNumber } from '../../helpers';
import { MFATextOption } from './mfa-text-option';

interface IMFASetupOptions {
  mfaTypes: IDropdownResponse[];
  setUser?: (user: IUser) => void;
  handleValidateNewMFA?: () => void;
  showAlertMessage?: boolean;
  finalButtonText?: string;
  setShowSetupButton?: (val: boolean) => void;
}

export const MFASetupOptions: FC<IMFASetupOptions> = ({
  finalButtonText,
  mfaTypes,
  setUser,
  handleValidateNewMFA,
  showAlertMessage = true,
  setShowSetupButton
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // state
  const [selectedMfaType, setSelectedMfaType] = useState<string>('');
  const [selectedPhoneNumber, setSelecedPhoneNumber] = useState<string>('');
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [selectedName, setSelectedName] = useState<string>('');
  const [appAuthOption, setAppAuthOption] = useState<ICreateMFAAppOptionRes | null>(null);
  const [textAuthOption, setTextAuthOption] = useState<string>('');
  const [isLoadingMFA, setIsLoadingMFA] = useState<boolean>(false);

  const handleTextSubmit = async () => {
    try {
      setIsLoadingMFA(true);
      if (textAuthOption) {
        await validateNewMFA(textAuthOption, selectedCode);

        if (setUser) {
          const user = await getMe();
          setUser(user);
        }
        // callback function and then reset state, used on the MFA settings section of user settings page
        if (handleValidateNewMFA) {
          setSelectedCode('');
          setSelectedMfaType('');
          setAppAuthOption(null);
          setTextAuthOption('');
          handleValidateNewMFA();
        }
      } else {
        const authCode =
          selectedMfaType === 'Authy'
            ? await createMFAAuthyOption(selectedPhoneNumber)
            : await createMFATextOption(selectedPhoneNumber);
        setTextAuthOption(authCode);
        enqueueSnackbar('Code Sent!', {
          variant: 'success'
        });
      }
      setShowSetupButton && setShowSetupButton(false);
    } catch (error) {
      enqueueSnackbar(
        error?.Detail ??
          (textAuthOption
            ? 'Error validating code, please try again.'
            : 'Error sending MFA code, please try again.'),
        {
          variant: 'error'
        }
      );
    } finally {
      setIsLoadingMFA(false);
    }
  };

  const handleAppSubmit = async () => {
    try {
      setIsLoadingMFA(true);
      const appAuthOption = await createMFAAppOption(selectedName);

      setAppAuthOption(appAuthOption);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error sending MFA code', {
        variant: 'error'
      });
    } finally {
      setIsLoadingMFA(false);
    }
  };

  const handleLogin = async () => {
    try {
      setIsLoadingMFA(true);
      await validateNewMFA(appAuthOption.twoFactorAuthenticationId, selectedCode);
      if (setUser) {
        const user = await getMe();
        setUser(user);
      }
      if (handleValidateNewMFA) {
        handleValidateNewMFA();
        enqueueSnackbar('MFA option setup!', {
          variant: 'success'
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error logging in, please try again.', {
        variant: 'error'
      });
    } finally {
      setSelectedCode('');
      setSelectedMfaType('');
      setAppAuthOption(null);
      setTextAuthOption('');
      setIsLoadingMFA(false);
    }
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();

        if (selectedMfaType === 'Text' || selectedMfaType === 'Authy') {
          return handleTextSubmit();
        }
        if (selectedMfaType === 'App' && !appAuthOption) {
          return handleAppSubmit();
        }
        if (appAuthOption) {
          return handleLogin();
        }
      }}
    >
      <Box mt={1}>
        {!textAuthOption && showAlertMessage && (
          <Box mb={1}>
            <Alert severity="info">
              {appAuthOption
                ? 'Scan the QR code in your authenticator app.'
                : 'Select an multifactor authentication option below to setup.'}
            </Alert>
          </Box>
        )}

        {!appAuthOption && !textAuthOption && (
          <>
            <Typography gutterBottom>Authenticator App</Typography>
            <Typography paragraph sx={{ fontSize: '0.85rem' }}>
              Use an authenticator app like Google Authenticator or Authy. This method is more
              secure, but requires an additional app to be installed on your device. To login you
              will open the authenticator app and enter the code on screen.
            </Typography>
            <Typography gutterBottom>Text Message (SMS)</Typography>
            <Typography paragraph sx={{ fontSize: '0.85rem' }}>
              Use text based two factor authentication. This method is more convenient but less
              secure. To login we will send a text to your phone number and you will enter the code
              proviced.
            </Typography>

            <SMBSelect
              label="Type"
              value={selectedMfaType}
              onChange={({ target: { value } }) => {
                setSelectedMfaType(value);
                setSelecedPhoneNumber('');
                setSelectedCode('');
              }}
            >
              {mfaTypes?.map((type, index) => {
                return (
                  <MenuItem key={`${index}`} value={type.value}>
                    {type.description}
                  </MenuItem>
                );
              })}
            </SMBSelect>
          </>
        )}
        {(selectedMfaType === 'Text' || selectedMfaType === 'Authy') && (
          <MFATextOption
            buttonText={
              textAuthOption && !setUser ? finalButtonText : textAuthOption ? 'Login' : 'Send Code'
            }
            isButtonDisabled={
              (textAuthOption ? !selectedCode : !selectedPhoneNumber) || isLoadingMFA
            }
            isLoading={isLoadingMFA}
            textFieldLabel={textAuthOption ? 'Code' : 'Phone Number'}
            isTextOption={selectedMfaType === 'Text'}
            textFieldValue={textAuthOption ? selectedCode : selectedPhoneNumber}
            authId={textAuthOption}
            handleChange={(val: string) => {
              if (textAuthOption) {
                setSelectedCode(val);
              } else {
                setSelecedPhoneNumber(formatInputPhoneNumber(val));
              }
            }}
            handleButtonClick={handleTextSubmit}
          />
        )}
        {selectedMfaType === 'App' && !appAuthOption && (
          <>
            <FormControl margin="normal" fullWidth>
              <TextField
                fullWidth
                id="name"
                label="Name"
                margin="none"
                required
                type="text"
                size="small"
                helperText="Name the MFA app option"
                onChange={e => {
                  setSelectedName(e.target.value);
                }}
                value={selectedName}
              />
            </FormControl>
            <FormControl fullWidth={true} margin="normal">
              <Button
                id="proceed"
                loading={isLoadingMFA ? 'Loading...' : ''}
                variant="contained"
                color="primary"
                startIcon={<ArrowForward />}
                onClick={handleAppSubmit}
                disabled={!selectedName}
              >
                {isLoadingMFA ? '' : 'Proceed'}
              </Button>
            </FormControl>
          </>
        )}
        {appAuthOption && (
          <>
            <Box display="flex" alignItems="center" justifyContent="center">
              <img src={appAuthOption.image} alt="" />
            </Box>
            <FormControl margin="normal" fullWidth>
              <TextField
                fullWidth
                id="code"
                label="Code"
                margin="none"
                required
                type="text"
                size="small"
                onChange={e => {
                  setSelectedCode(e.target.value.trim());
                }}
                value={selectedCode}
              />
            </FormControl>
            <FormControl fullWidth={true} margin="normal">
              <Button
                disabled={!selectedCode || isLoadingMFA}
                type="submit"
                onClick={handleLogin}
                color="primary"
                fullWidth
                id="app-code"
                variant="contained"
                loading={isLoadingMFA ? 'Loading...' : ''}
              >
                {isLoadingMFA ? '' : setUser ? 'Login' : 'Save'}
              </Button>
            </FormControl>
          </>
        )}
      </Box>
    </form>
  );
};
